import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DETAIL_TYPES, resolveDetailType, resolveTileType, TILE_TYPES, TILE_KIND_BASE, TILE_KIND_DETAIL } from '../../GameWorld/tiles';
import {
  buildWorldAlignedBaseStyle,
  detailRenderDimensionsPixels,
  normalizeTileData,
  getDefaultBaseTiles,
  getDefaultDetailTiles,
} from '../../GameWorld/tileUtils';
import {
  calculateMapTransform,
  screenPointToTile,
} from '../../GameWorld/mapRenderModel';
import { supportsWebGL2 } from '../../GameWorld/TileMap';
import useImageCache from '../../../hooks/useImageCache';
import AdminAPI from '../AdminAPI';
import { MEDIA_VARIANTS } from '../../../media';
import '../../GameWorld/TileMap.css';

const CAMERA_TILT_DEGREES = 30;
const SPRITE_PITCH_DEGREES = 75;
const RENDER_PADDING_TILES = 2;
const EMPTY_LIST = Object.freeze([]);
const EMPTY_SELECTION = Object.freeze({});

function RegionMapEditor({
  activeBaseTile,
  activeDetail,
  activeWalkableOverride = null,
  campEntrance = null,
  detailTypes = [],
  enemyPreviewPlacements = [],
  enemyRoamingTileIndexes = [],
  enemySpawnTileIndexes = [],
  exits = [],
  isSettingSpawn = false,
  lootContainers = [],
  lootContainerTypes = [],
  map,
  onPaint,
  onPlaceMarker,
  onSelectEnemyPreview = () => {},
  onSetSpawn = () => {},
  placementMode = '',
  resourceItems = [],
  resourceNodes = [],
  selectedEnemyPreviewId = '',
  showWorldShroud = false,
  showWalkabilityOverlay = false,
  spawnLocation = { x: 0, y: 0 },
  tileTypes = [],
}) {
  const [RendererComponent, setRendererComponent] = useState(null);
  const [rendererState, setRendererState] = useState(() => (
    supportsWebGL2() ? 'loading' : 'unsupported'
  ));
  const isPaintingRef = useRef(false);
  const lastPaintedTileRef = useRef('');
  const fetchImage = useCallback((id, variant) => AdminAPI.fetchImage(id, variant), []);
  const getImageUrl = useImageCache(fetchImage);
  const activePlacementMode = placementMode || (isSettingSpawn ? 'spawn' : '');

  useEffect(() => {
    function handleUp() {
      isPaintingRef.current = false;
      lastPaintedTileRef.current = '';
    }
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('blur', handleUp);
    return () => {
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('blur', handleUp);
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!supportsWebGL2()) return undefined;
    import('../../GameWorld/WebGLWorldRenderer')
      .then(module => {
        if (active) setRendererComponent(() => module.default);
      })
      .catch(() => {
        if (active) setRendererState('error');
      });
    return () => { active = false; };
  }, []);

  const handleDown = (x, y, event) => {
    if (event) {
      event.preventDefault();
    }
    if (activePlacementMode) {
      if (onPlaceMarker) {
        onPlaceMarker(x, y);
      } else if (activePlacementMode === 'spawn') {
        onSetSpawn(x, y);
      }
      isPaintingRef.current = false;
      return;
    }
    isPaintingRef.current = true;
    lastPaintedTileRef.current = `${x}:${y}`;
    onPaint(
      x,
      y,
      activeBaseTile,
      activeDetail,
      activeWalkableOverride,
    );
  };

  const handleEnter = (x, y, event) => {
    if (event) {
      event.preventDefault();
    }
    if (!isPaintingRef.current) return;
    if (activePlacementMode) return;
    const tileKey = `${x}:${y}`;
    if (lastPaintedTileRef.current === tileKey) return;
    lastPaintedTileRef.current = tileKey;
    onPaint(
      x,
      y,
      activeBaseTile,
      activeDetail,
      activeWalkableOverride,
    );
  };

  const tileSize = 24;

  const defaultBaseTiles = useMemo(() => getDefaultBaseTiles(), []);
  const defaultDetailTiles = useMemo(
    () => getDefaultDetailTiles().filter((tile) => tile.type === TILE_KIND_DETAIL),
    [],
  );

  const tileLookup = useMemo(() => {
    const lookup = {};
    const register = (tile) => {
      const normalized = normalizeTileData(tile, TILE_KIND_BASE);
      if (!normalized || normalized.type !== TILE_KIND_BASE) return;
      lookup[normalized.id] = normalized;
      lookup[normalized.key] = normalized;
    };

    defaultBaseTiles.forEach(register);

    if (Array.isArray(tileTypes)) {
      tileTypes.forEach(register);
    }

    return lookup;
  }, [defaultBaseTiles, tileTypes]);

  const detailLookup = useMemo(() => {
    const lookup = {};
    const register = (detail) => {
      const normalized = normalizeTileData(detail, TILE_KIND_DETAIL);
      if (!normalized || normalized.type !== TILE_KIND_DETAIL) return;
      lookup[normalized.id] = normalized;
      lookup[normalized.key] = normalized;
    };

    defaultDetailTiles.forEach(register);

    if (Array.isArray(detailTypes)) {
      detailTypes.forEach(register);
    }

    return lookup;
  }, [defaultDetailTiles, detailTypes]);

  const fallbackBaseTile = useMemo(
    () => tileLookup[TILE_TYPES.sand.id] || normalizeTileData(resolveTileType('sand'), TILE_KIND_BASE),
    [tileLookup],
  );

  const exitsByTile = useMemo(() => {
    const lookup = new Map();
    exits.forEach((exit) => {
      const x = Number(exit?.x ?? exit?.X);
      const y = Number(exit?.y ?? exit?.Y);
      if (!Number.isInteger(x) || !Number.isInteger(y)) return;
      const coordinate = `${x}:${y}`;
      lookup.set(coordinate, [...(lookup.get(coordinate) || []), exit]);
    });
    return lookup;
  }, [exits]);

  const resourcesByTile = useMemo(() => {
    const lookup = new Map();
    resourceNodes.forEach((resource) => {
      const x = Number(resource?.x ?? resource?.X);
      const y = Number(resource?.y ?? resource?.Y);
      if (!Number.isInteger(x) || !Number.isInteger(y)) return;
      const coordinate = `${x}:${y}`;
      lookup.set(coordinate, [...(lookup.get(coordinate) || []), resource]);
    });
    return lookup;
  }, [resourceNodes]);

  const resourceItemsById = useMemo(() => new Map(resourceItems.map(item => [
    String(item?.itemId ?? item?.ItemId ?? ''),
    item,
  ])), [resourceItems]);
  const lootTypesById = useMemo(() => new Map(lootContainerTypes.map(type => [
    String(type?.id ?? type?.Id ?? ''),
    type,
  ])), [lootContainerTypes]);
  const lootByTile = useMemo(() => {
    const lookup = new Map();
    lootContainers.forEach((container) => {
      const x = Number(container?.x ?? container?.X);
      const y = Number(container?.y ?? container?.Y);
      if (!Number.isInteger(x) || !Number.isInteger(y)) return;
      lookup.set(`${x}:${y}`, [
        ...(lookup.get(`${x}:${y}`) || []),
        container,
      ]);
    });
    return lookup;
  }, [lootContainers]);

  const resolvedRows = useMemo(() => map.tiles.map((row, rowIndex) => (
    row.map((tile, colIndex) => {
      const baseKey = tile?.base ?? tile?.Base;
      const detailKey = tile?.detail ?? tile?.Detail;
      const walkableOverride = tile?.walkableOverride
        ?? tile?.WalkableOverride
        ?? null;
      let baseTile = baseKey ? tileLookup[baseKey] : null;
      if (!baseTile) {
        const resolvedBase = normalizeTileData(resolveTileType(baseKey), TILE_KIND_BASE);
        if (resolvedBase && resolvedBase.type === TILE_KIND_BASE) {
          baseTile = resolvedBase;
        } else {
          baseTile = fallbackBaseTile;
        }
      }

      let detailTile = null;
      if (detailKey && detailKey !== 'none') {
        detailTile = detailLookup[detailKey] || null;
        if (!detailTile) {
          const resolvedDetail = normalizeTileData(
            resolveDetailType(detailKey) || DETAIL_TYPES[detailKey],
            TILE_KIND_DETAIL,
          );
          if (resolvedDetail && resolvedDetail.type === TILE_KIND_DETAIL) {
            detailTile = resolvedDetail;
          }
        }
      }

      const baseImageUrl = baseTile?.imageId
        ? getImageUrl(baseTile.imageId, MEDIA_VARIANTS.TILE_TEXTURE)
        : null;
      const detailImageUrl = detailTile?.imageId
        ? getImageUrl(detailTile.imageId, MEDIA_VARIANTS.TILE_DETAIL)
        : null;

      return {
        baseTile: {
          ...baseTile,
          imageUrl: baseImageUrl,
          walkable: walkableOverride ?? baseTile?.walkable,
        },
        baseStyle: {
          width: tileSize,
          height: tileSize,
          backgroundColor: baseTile?.fallbackColor || '#6b7280',
          ...buildWorldAlignedBaseStyle(baseImageUrl, colIndex, rowIndex, tileSize),
        },
        colIndex,
        detailImageUrl,
        detailTile: detailTile
          ? { ...detailTile, imageUrl: detailImageUrl }
          : null,
        isSpawn: spawnLocation
          && spawnLocation.x === colIndex
          && spawnLocation.y === rowIndex,
        isCamp: campEntrance
          && Number(campEntrance.x) === colIndex
          && Number(campEntrance.y) === rowIndex,
        exits: exitsByTile.get(`${colIndex}:${rowIndex}`) || [],
        loot: lootByTile.get(`${colIndex}:${rowIndex}`) || [],
        resources: resourcesByTile.get(`${colIndex}:${rowIndex}`) || [],
        rowIndex,
        walkableOverride,
      };
    })
  )), [
    campEntrance,
    detailLookup,
    exitsByTile,
    fallbackBaseTile,
    getImageUrl,
    lootByTile,
    map.tiles,
    resourcesByTile,
    spawnLocation,
    tileLookup,
  ]);

  const terrainRows = useMemo(() => resolvedRows.map(row => row.map(cell => ({
    base: cell.baseTile,
    detail: cell.detailTile,
  }))), [resolvedRows]);
  const detailEntities = useMemo(() => resolvedRows.flatMap(row => (
    row.filter(cell => cell.detailTile)
  )), [resolvedRows]);
  const rendererDetails = useMemo(() => detailEntities.map(cell => ({
    ...cell.detailTile,
    id: `${cell.rowIndex}:${cell.colIndex}:${cell.detailTile.id}`,
    x: cell.colIndex,
    y: cell.rowIndex,
  })), [detailEntities]);
  const resourceEntities = useMemo(() => resourceNodes.flatMap((resource, index) => {
    const x = Number(resource?.x ?? resource?.X);
    const y = Number(resource?.y ?? resource?.Y);
    if (
      !Number.isInteger(x)
      || !Number.isInteger(y)
      || x < 0
      || y < 0
      || x >= map.width
      || y >= map.height
    ) return [];
    const itemId = String(resource?.itemId ?? resource?.ItemId ?? '');
    const item = resourceItemsById.get(itemId);
    const imageId = item?.mapImageId ?? item?.MapImageId ?? '';
    return [{
      depth: 5,
      id: `resource:${resource?.placementId ?? resource?.PlacementId ?? index}`,
      imageUrl: imageId
        ? getImageUrl(imageId, MEDIA_VARIANTS.ENTITY_ICON)
        : null,
      key: resource?.placementId
        ?? resource?.PlacementId
        ?? `${itemId}-${x}-${y}-${index}`,
      name: (item?.name ?? item?.Name ?? itemId) || 'Resource',
      kind: 'resource',
      visualSize: 44,
      x,
      y,
    }];
  }), [getImageUrl, map.height, map.width, resourceItemsById, resourceNodes]);
  const lootEntities = useMemo(() => lootContainers.flatMap((container, index) => {
    const x = Number(container?.x ?? container?.X);
    const y = Number(container?.y ?? container?.Y);
    if (
      !Number.isInteger(x)
      || !Number.isInteger(y)
      || x < 0
      || y < 0
      || x >= map.width
      || y >= map.height
    ) return [];
    const typeId = String(
      container?.containerTypeId ?? container?.ContainerTypeId ?? '',
    );
    const type = lootTypesById.get(typeId);
    const imageId = container?.closedImageId
      ?? container?.ClosedImageId
      ?? type?.closedImageId
      ?? type?.ClosedImageId
      ?? '';
    return [{
      depth: 7,
      id: `world-loot:${container?.placementId ?? container?.PlacementId ?? index}`,
      imageUrl: imageId
        ? getImageUrl(imageId, MEDIA_VARIANTS.TILE_DETAIL)
        : null,
      key: container?.runtimeKey
        ?? container?.RuntimeKey
        ?? container?.placementId
        ?? container?.PlacementId
        ?? `${typeId}-${x}-${y}-${index}`,
      name: container?.name
        ?? container?.Name
        ?? type?.name
        ?? type?.Name
        ?? typeId
        ?? 'Loot container',
      kind: 'worldLoot',
      scale: Number(
        container?.renderScaleTiles
        ?? container?.RenderScaleTiles
        ?? type?.renderScaleTiles
        ?? type?.RenderScaleTiles,
      ) || 1,
      visualSize: Math.max(32, tileSize * (Number(
        container?.renderScaleTiles
        ?? container?.RenderScaleTiles
        ?? type?.renderScaleTiles
        ?? type?.RenderScaleTiles,
      ) || 1)),
      x,
      y,
    }];
  }), [getImageUrl, lootContainers, lootTypesById, map.height, map.width]);
  const enemyPreviewEntities = useMemo(() => enemyPreviewPlacements.flatMap(
    (placement, index) => {
      const x = Number(placement?.x ?? placement?.X);
      const y = Number(placement?.y ?? placement?.Y);
      if (
        !Number.isInteger(x)
        || !Number.isInteger(y)
        || x < 0
        || y < 0
        || x >= map.width
        || y >= map.height
      ) return [];
      const imageId = placement?.imageId ?? placement?.ImageId ?? '';
      return [{
        imageUrl: imageId
          ? getImageUrl(imageId, MEDIA_VARIANTS.ENTITY_ICON)
          : null,
        instanceId: placement?.instanceId
          ?? placement?.InstanceId
          ?? `enemy-preview-${index}`,
        isChampion: Boolean(
          placement?.isChampion ?? placement?.IsChampion,
        ),
        name: placement?.name ?? placement?.Name ?? 'Enemy',
        placement,
        x,
        y,
      }];
    },
  ), [enemyPreviewPlacements, getImageUrl, map.height, map.width]);

  const editorObjects = useMemo(() => [
    ...(campEntrance ? [{
      depth: 9,
      id: 'camp-entrance',
      kind: 'camp',
      visualSize: 54,
      x: Number(campEntrance.x),
      y: Number(campEntrance.y),
    }] : []),
    ...exits.map((exit, index) => ({
      depth: 9,
      direction: String(exit?.direction ?? exit?.Direction ?? 'passage').toLowerCase(),
      disabled: (exit?.enabled ?? exit?.Enabled) === false,
      id: `exit:${exit?.key ?? exit?.Key ?? index}`,
      kind: 'exit',
      visualSize: 54,
      x: Number(exit?.x ?? exit?.X),
      y: Number(exit?.y ?? exit?.Y),
    })),
    ...resourceEntities,
    ...lootEntities,
  ].filter((object) => (
    Number.isInteger(object.x)
    && Number.isInteger(object.y)
    && object.x >= 0
    && object.y >= 0
    && object.x < map.width
    && object.y < map.height
  )), [
    campEntrance,
    exits,
    lootEntities,
    map.height,
    map.width,
    resourceEntities,
  ]);
  const tiltScaleY = Math.cos(CAMERA_TILT_DEGREES * (Math.PI / 180));
  const renderPadding = tileSize * RENDER_PADDING_TILES;
  const rendererWidth = (map.width * tileSize) + (renderPadding * 2);
  const rendererHeight = Math.ceil(
    (map.height * tileSize * tiltScaleY) + (renderPadding * 2),
  );
  const rendererTransform = useMemo(() => calculateMapTransform({
    cameraOffset: {
      x: -renderPadding,
      y: -renderPadding / tiltScaleY,
    },
    mapHeight: map.height,
    mapWidth: map.width,
    tileSize,
    verticalScaleY: tiltScaleY,
    viewportHeight: rendererHeight,
    viewportWidth: rendererWidth,
  }), [
    map.height,
    map.width,
    renderPadding,
    rendererHeight,
    rendererWidth,
    tiltScaleY,
  ]);
  const walkabilityOverlayCells = useMemo(
    () => resolvedRows.flatMap(row => row.map(cell => ({
      inherited: cell.walkableOverride === null,
      walkable: Boolean(cell.baseTile?.walkable),
      x: cell.colIndex,
      y: cell.rowIndex,
    }))),
    [resolvedRows],
  );
  const enemyAreaOverlayCells = useMemo(() => {
    const cells = new Map();
    const add = (indexes, property) => {
      indexes.forEach((value) => {
        const index = Number(value);
        if (!Number.isInteger(index) || index < 0) return;
        const x = index % map.width;
        const y = Math.floor(index / map.width);
        if (y >= map.height) return;
        const cell = cells.get(index) || {
          index,
          roaming: false,
          spawn: false,
          x,
          y,
        };
        cell[property] = true;
        cells.set(index, cell);
      });
    };
    add(enemySpawnTileIndexes, 'spawn');
    add(enemyRoamingTileIndexes, 'roaming');
    return [...cells.values()];
  }, [enemyRoamingTileIndexes, enemySpawnTileIndexes, map.height, map.width]);
  const usesWebGLCanvas = Boolean(
    RendererComponent && rendererState !== 'error',
  );
  const webGLReady = rendererState === 'ready';
  const resolvePointerTile = (event, transform) => screenPointToTile({
    canvasRect: event.currentTarget.getBoundingClientRect(),
    clientX: event.clientX,
    clientY: event.clientY,
    mapHeight: map.height,
    mapWidth: map.width,
    tileSize,
    transform,
  });
  const handleCanvasPointerDown = (event, transform) => {
    const tile = resolvePointerTile(event, transform);
    if (!tile) return;
    handleDown(tile.x, tile.y, event);
  };
  const handleCanvasPointerMove = (event, transform) => {
    if (!isPaintingRef.current) return;
    const tile = resolvePointerTile(event, transform);
    if (!tile) return;
    handleEnter(tile.x, tile.y, event);
  };

  return (
    <div
      className={[
        'worldeditor-canvas',
        activePlacementMode ? 'worldeditor-canvas--placing-marker' : '',
        webGLReady ? 'worldeditor-canvas--webgl' : '',
      ].filter(Boolean).join(' ')}
      data-placement-mode={activePlacementMode || undefined}
      data-renderer={webGLReady ? 'three-webgl2' : 'dom-fallback'}
      style={{
        height: usesWebGLCanvas ? rendererHeight : map.height * tileSize,
        width: usesWebGLCanvas ? rendererWidth : map.width * tileSize,
      }}
    >
      {usesWebGLCanvas ? (
        <RendererComponent
          avatarSelection={EMPTY_SELECTION}
          cameraTiltDegrees={CAMERA_TILT_DEGREES}
          details={rendererDetails}
          dropTiles={EMPTY_LIST}
          focusedObjectId={null}
          height={rendererHeight}
          hoveredObjectId={null}
          keyboardCursor={null}
          movementTarget={null}
          objects={editorObjects}
          onlinePlayers={EMPTY_LIST}
          onCanvasPointerDown={handleCanvasPointerDown}
          onCanvasPointerMove={handleCanvasPointerMove}
          onError={() => setRendererState('error')}
          onReady={() => setRendererState('ready')}
          playerPosition={spawnLocation}
          queuedDestination={spawnLocation}
          reducedMotion={Boolean(
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
          )}
          scavengeRadius={0}
          selectedObjectId={null}
          showPlayer={false}
          showWorldShroud={showWorldShroud}
          spritePitchDegrees={SPRITE_PITCH_DEGREES}
          terrainRows={terrainRows}
          tileSize={tileSize}
          transform={rendererTransform}
          width={rendererWidth}
        />
      ) : null}
      {!webGLReady ? resolvedRows.map((row, rowIndex) => (
        <div className="worldeditor-row" key={`row-${rowIndex}`}>
          {row.map(({
            baseStyle,
            colIndex,
            exits: tileExits,
            isCamp,
            isSpawn,
            resources,
            loot,
          }) => {
            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={[
                  'worldeditor-tile',
                  isSpawn ? 'worldeditor-tile--spawn' : '',
                  isCamp ? 'worldeditor-tile--camp' : '',
                  tileExits.length ? 'worldeditor-tile--exit' : '',
                  resources.length ? 'worldeditor-tile--resource' : '',
                  loot.length ? 'worldeditor-tile--loot' : '',
                ].filter(Boolean).join(' ')}
                style={baseStyle}
                onMouseDown={(event) => handleDown(colIndex, rowIndex, event)}
                onMouseEnter={(event) => handleEnter(colIndex, rowIndex, event)}
              >
                {isSpawn && <div className="worldeditor-spawn-flag" aria-label="Spawn Point" />}
                <span className="worldeditor-marker-stack" aria-hidden="true">
                  {isCamp && (
                    <i className="worldeditor-marker worldeditor-marker--camp">C</i>
                  )}
                  {tileExits.map((exit, index) => (
                    <i
                      className={[
                        'worldeditor-marker',
                        'worldeditor-marker--exit',
                        (exit.enabled ?? exit.Enabled) === false
                          ? 'is-disabled'
                          : '',
                      ].filter(Boolean).join(' ')}
                      key={`${exit.key ?? exit.Key ?? 'exit'}-${index}`}
                    >
                      E
                    </i>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )) : null}
      {showWalkabilityOverlay ? (
        <div className="worldeditor-walkability-layer" aria-hidden="true">
          {walkabilityOverlayCells.map(cell => (
            <span
              className={[
                'worldeditor-walkability-cell',
                cell.walkable
                  ? 'worldeditor-walkability-cell--walkable'
                  : 'worldeditor-walkability-cell--blocked',
                cell.inherited
                  ? 'worldeditor-walkability-cell--inherited'
                  : 'worldeditor-walkability-cell--override',
              ].join(' ')}
              key={`${cell.x}:${cell.y}`}
              style={{
                height: usesWebGLCanvas ? tileSize * tiltScaleY : tileSize,
                left: (usesWebGLCanvas ? renderPadding : 0) + cell.x * tileSize,
                top: (usesWebGLCanvas ? renderPadding : 0)
                  + cell.y * tileSize * (usesWebGLCanvas ? tiltScaleY : 1),
                width: tileSize,
              }}
            />
          ))}
        </div>
      ) : null}
      {enemyAreaOverlayCells.length ? (
        <div className="worldeditor-enemy-area-layer" aria-hidden="true">
          {enemyAreaOverlayCells.map(cell => (
            <span
              className={[
                'worldeditor-enemy-area-cell',
                cell.spawn
                  ? 'worldeditor-enemy-area-cell--spawn'
                  : '',
                cell.roaming
                  ? 'worldeditor-enemy-area-cell--roaming'
                  : '',
              ].filter(Boolean).join(' ')}
              key={cell.index}
              style={{
                height: usesWebGLCanvas ? tileSize * tiltScaleY : tileSize,
                left: (usesWebGLCanvas ? renderPadding : 0) + cell.x * tileSize,
                top: (usesWebGLCanvas ? renderPadding : 0)
                  + cell.y * tileSize * (usesWebGLCanvas ? tiltScaleY : 1),
                width: tileSize,
              }}
            />
          ))}
        </div>
      ) : null}
      {enemyPreviewEntities.length ? (
        <div className="worldeditor-enemy-layer">
          {enemyPreviewEntities.map(enemy => (
            <button
              aria-label={`${enemy.name}${enemy.isChampion ? ' Champion' : ''} preview spawn at ${enemy.x}, ${enemy.y}`}
              aria-pressed={selectedEnemyPreviewId === enemy.instanceId}
              className={[
                'worldeditor-enemy-entity',
                enemy.imageUrl
                  ? 'worldeditor-enemy-entity--image'
                  : '',
                selectedEnemyPreviewId === enemy.instanceId
                  ? 'is-selected'
                  : '',
              ].filter(Boolean).join(' ')}
              key={enemy.instanceId}
              onClick={(event) => {
                event.stopPropagation();
                onSelectEnemyPreview(enemy.placement);
              }}
              style={{
                backgroundImage: enemy.imageUrl
                  ? `url(${enemy.imageUrl})`
                  : undefined,
                left: (usesWebGLCanvas ? renderPadding : 0)
                  + (enemy.x + 0.5) * tileSize,
                top: (usesWebGLCanvas ? renderPadding : 0)
                  + (enemy.y + 0.5) * tileSize
                    * (usesWebGLCanvas ? tiltScaleY : 1),
              }}
              type="button"
            >
              {enemy.imageUrl ? null : 'E'}
            </button>
          ))}
        </div>
      ) : null}
      {!webGLReady ? <div className="worldeditor-entity-layer" aria-hidden="true">
        {detailEntities.map(({
          colIndex,
          detailImageUrl,
          detailTile,
          rowIndex,
        }) => {
          const renderSize = detailRenderDimensionsPixels(
            tileSize,
            detailTile.renderWidthTiles,
            detailTile.renderHeightTiles,
            detailTile.renderScaleTiles,
          );
          const detailStyle = {
            left: (colIndex + 0.5) * tileSize,
            top: (rowIndex + 0.5) * tileSize,
            width: renderSize.width,
            height: renderSize.height,
            zIndex: (rowIndex * 2) + 1,
          };
          if (detailImageUrl) {
            detailStyle.backgroundImage = `url(${detailImageUrl})`;
          }

          return (
            <span
              className="game-map-detail-entity tile-detail--editor"
              data-owner-col={colIndex}
              data-owner-row={rowIndex}
              data-render-scale={detailTile.renderScaleTiles}
              data-render-width={detailTile.renderWidthTiles}
              data-render-height={detailTile.renderHeightTiles}
              key={`${rowIndex}-${colIndex}-${detailTile.id || detailTile.key || 'detail'}`}
              style={detailStyle}
            >
              {!detailImageUrl && (
                <span
                  className="game-map-detail-entity__fallback"
                  style={{ backgroundColor: detailTile.fallbackColor || 'rgba(0,0,0,0.35)' }}
                />
              )}
            </span>
          );
        })}
      </div> : null}
      {!webGLReady ? <div className="worldeditor-resource-layer">
        {resourceEntities.map(resource => (
          <span
            aria-label={`${resource.name} resource at ${resource.x}, ${resource.y}`}
            className={[
              'worldeditor-resource-entity',
              resource.imageUrl ? 'worldeditor-resource-entity--image' : '',
            ].filter(Boolean).join(' ')}
            key={resource.key}
            role="img"
            style={{
              backgroundImage: resource.imageUrl
                ? `url(${resource.imageUrl})`
                : undefined,
              left: (resource.x + 0.5) * tileSize,
              top: (resource.y + 0.5) * tileSize,
            }}
          >
            {resource.imageUrl ? null : 'R'}
          </span>
        ))}
      </div> : null}
      {!webGLReady ? <div className="worldeditor-loot-layer">
        {lootEntities.map(container => (
          <span
            aria-label={`${container.name} loot container at ${container.x}, ${container.y}`}
            className={[
              'worldeditor-loot-entity',
              container.imageUrl ? 'worldeditor-loot-entity--image' : '',
            ].filter(Boolean).join(' ')}
            key={container.key}
            role="img"
            style={{
              backgroundImage: container.imageUrl
                ? `url(${container.imageUrl})`
                : undefined,
              height: tileSize * container.scale,
              left: (container.x + 0.5) * tileSize,
              top: (container.y + 0.5) * tileSize,
              width: tileSize * container.scale,
              zIndex: (container.y * 2) + 2,
            }}
          >
            {container.imageUrl ? null : 'L'}
          </span>
        ))}
      </div> : null}
    </div>
  );
}

export default RegionMapEditor;
